import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Elements, CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import CommonModal, {
  CommonModalFooter,
} from "../shared/modalLayout/CommonModal";
import Button from "../shared/buttons/button";
import AudiologistDeliveryFields from "./AudiologistDeliveryFields";
import { useConfirmVaOrder } from "../../hooks/useConfirmVaOrder";
import { useQuoteAudiologistOrderMutation } from "../../app/vaApi/vaApi";
import {
  buildAudiologistOrderSummary,
  formatUsd,
} from "../../helpers/audiologistOrderSummary";
import { getConfirmNavigationOrderId } from "../../helpers/vaInvoiceUtils";
import {
  getAudiologistStripePromise,
  isStripeCheckoutConfigured,
} from "../../helpers/audiologistStripe";
import { isMultiLensPrescription } from "../../helpers/prescriptionFormat";
import { showToast } from "../../helpers/Utils";
import { useAuth } from "../../features/auth/AuthContext";
import "../../styles/orderConfirmationModal.css";

const EMPTY_DELIVERY = {
  firstName: "",
  lastName: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  email: "",
};

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "14px",
      color: "#101828",
      "::placeholder": { color: "#98A2B3" },
    },
    invalid: { color: "#D92D20" },
  },
};

function getDeliveryErrors(delivery) {
  const errors = {};
  if (!String(delivery.firstName || "").trim()) errors.firstName = true;
  if (!String(delivery.lastName || "").trim()) errors.lastName = true;
  if (!String(delivery.address1 || "").trim()) errors.address1 = true;
  if (!String(delivery.city || "").trim()) errors.city = true;
  if (!String(delivery.state || "").trim()) errors.state = true;
  if (!String(delivery.zip || "").trim()) errors.zip = true;
  if (!String(delivery.phone || "").trim()) errors.phone = true;
  if (!String(delivery.email || "").trim()) errors.email = true;
  return errors;
}

function ConfirmationCheckoutForm({
  show,
  onClose,
  onBack,
  skuCode,
  skuLabel,
  skuPrice,
  bundleCode,
  bundle = null,
  lensSelection,
  prescriptionData,
  initialDelivery,
  onDeliveryChange,
}) {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const audiologistEmail = String(user?.email || "").trim();
  const { confirm, isSubmitting } = useConfirmVaOrder();
  const [quoteOrder, { isLoading: isQuoting }] =
    useQuoteAudiologistOrderMutation();
  const [delivery, setDelivery] = useState(() => ({
    ...EMPTY_DELIVERY,
    ...(initialDelivery || {}),
  }));
  const [deliveryErrors, setDeliveryErrors] = useState({});
  const [quote, setQuote] = useState(null);
  const [isPaying, setIsPaying] = useState(false);
  const hasHydratedDeliveryRef = useRef(false);

  useEffect(() => {
    if (!show) {
      hasHydratedDeliveryRef.current = false;
      return;
    }
    if (hasHydratedDeliveryRef.current) return;
    hasHydratedDeliveryRef.current = true;
    setDelivery({
      ...EMPTY_DELIVERY,
      ...(initialDelivery || {}),
    });
  }, [show, initialDelivery]);

  const summary = useMemo(
    () =>
      buildAudiologistOrderSummary({
        skuCode,
        skuLabel,
        skuPrice,
        bundleCode,
        bundle,
        lensSelection,
        prescriptionData,
        quote,
      }),
    [
      skuCode,
      skuLabel,
      skuPrice,
      bundleCode,
      bundle,
      lensSelection,
      prescriptionData,
      quote,
    ]
  );

  const syncDelivery = (next) => {
    setDelivery(next);
    onDeliveryChange?.(next);
  };

  useEffect(() => {
    if (!show || !bundleCode) {
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await quoteOrder({
          bundleCode,
          skuCode,
          delivery,
          prescriptionSkipped: Boolean(prescriptionData?.skipped),
          lensSelection,
        }).unwrap();
        if (!cancelled) {
          setQuote(result?.data || result);
        }
      } catch (_err) {
        if (!cancelled) setQuote(null);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    show,
    bundleCode,
    skuCode,
    delivery.firstName,
    delivery.lastName,
    delivery.address1,
    delivery.city,
    delivery.state,
    delivery.zip,
    prescriptionData?.skipped,
    lensSelection,
    quoteOrder,
  ]);

  const updateDeliveryField = (e) => {
    const { name, value } = e.target;
    const next = { ...delivery, [name]: value };
    syncDelivery(next);
    setDeliveryErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handlePlaceSelect = (place) => {
    const next = {
      ...delivery,
      address1: place.address1 ?? delivery.address1,
      city: place.city ?? delivery.city,
      state: place.state ?? delivery.state,
      zip: place.zip ?? delivery.zip,
    };
    syncDelivery(next);
    setDeliveryErrors((prev) => ({
      ...prev,
      address1: false,
      city: false,
      state: false,
      zip: false,
    }));
  };

  const handleBack = () => {
    onDeliveryChange?.(delivery);
    onBack?.();
  };

  const handleConfirmAndPay = async () => {
    const nextDeliveryErrors = getDeliveryErrors(delivery);
    setDeliveryErrors(nextDeliveryErrors);
    if (Object.keys(nextDeliveryErrors).length > 0) {
      showToast("Please fill in all required delivery fields", "error");
      return;
    }

    if (!isStripeCheckoutConfigured()) {
      showToast(
        "Stripe is not configured (VITE_STRIPE_PUBLISHABLE_KEY).",
        "error"
      );
      return;
    }

    if (!stripe || !elements) {
      showToast("Payment form is still loading. Try again.", "error");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      showToast("Enter card details to continue.", "error");
      return;
    }

    setIsPaying(true);
    try {
      const billingName = [delivery.firstName, delivery.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: billingName || undefined,
          email: delivery.email || undefined,
          phone: delivery.phone || undefined,
          address: {
            line1: delivery.address1 || undefined,
            line2: delivery.address2 || undefined,
            city: delivery.city || undefined,
            state: delivery.state || undefined,
            postal_code: delivery.zip || undefined,
            country: "US",
          },
        },
      });

      if (error || !paymentMethod?.id) {
        showToast(error?.message || "Could not process card.", "error");
        return;
      }

      const prescriptionSkipped = Boolean(prescriptionData?.skipped);
      const multiLens =
        !prescriptionSkipped && isMultiLensPrescription(prescriptionData);
      const confirmPayload = {
        bundleCode,
        skuCode,
        prescription: prescriptionSkipped || multiLens ? null : prescriptionData,
        prescriptions:
          prescriptionSkipped || !multiLens
            ? null
            : prescriptionData.prescriptions,
        prescriptionSkipped,
        status: prescriptionSkipped ? 0 : undefined,
        lensSelection,
        delivery,
        paymentMethodId: paymentMethod.id,
        prescriptionFileKey: prescriptionData?.prescriptionFileKey || null,
        manual: true,
      };

      let result;
      try {
        result = await confirm(confirmPayload);
      } catch (err) {
        const code = String(err?.data?.code || "").toUpperCase();
        const clientSecret = err?.data?.clientSecret;
        const paymentIntentId = err?.data?.paymentIntentId;

        // Live cards often require OTP / 3D Secure before charge succeeds
        if (
          code === "PAYMENT_REQUIRES_ACTION" &&
          clientSecret &&
          paymentIntentId
        ) {
          const { error: authError, paymentIntent } =
            await stripe.confirmCardPayment(clientSecret);
          if (authError) {
            showToast(
              authError.message || "Card authentication failed. Try again.",
              "error"
            );
            return;
          }
          if (paymentIntent?.status !== "succeeded") {
            showToast(
              "Payment was not completed after authentication. Try again.",
              "error"
            );
            return;
          }

          result = await confirm({
            ...confirmPayload,
            paymentIntentId: paymentIntent.id || paymentIntentId,
          });
        } else {
          throw err;
        }
      }

      const orderId = getConfirmNavigationOrderId(result);
      showToast("Order created successfully", "success");
      onClose();
      if (orderId) {
        navigate(`/orders/order-details/${orderId}`);
      } else {
        navigate("/orders");
      }
    } catch (err) {
      showToast(
        err?.data?.message || err?.message || "Failed to create order. Please try again.",
        "error"
      );
    } finally {
      setIsPaying(false);
    }
  };

  const busy = isSubmitting || isPaying;

  return (
    <CommonModal
      show={show}
      setShow={(open) => {
        if (!open) onClose();
      }}
      title="Confirmation"
      centered
      className="order-confirmation-modal modal-md"
      onHide={onClose}
      onBack={handleBack}
      closeOnBackdrop={false}
      footer={
        <CommonModalFooter className="order-confirmation-footer d-flex gap-3 mt-4">
          <Button
            label="Confirm and Pay"
            onClick={handleConfirmAndPay}
            loading={busy}
            disabled={busy || isQuoting}
          />
          <Button
            label="Cancel"
            className="cancel"
            onClick={onClose}
            disabled={busy}
          />
        </CommonModalFooter>
      }
    >
      <div className="order-confirmation">
        <h5 className="order-confirmation__title">{summary.productTitle}</h5>

        <dl className="order-confirmation__details">
          {summary.lines.map((line, index) => (
            <div
              key={`${line.label}-${index}`}
              className="order-confirmation__row"
            >
              <dt>{line.label}</dt>
              <dd>
                {line.isFile && line.href ? (
                  <button
                    type="button"
                    className="order-confirmation__file-link"
                    onClick={() =>
                      window.open(line.href, "_blank", "noopener,noreferrer")
                    }
                  >
                    {line.value}
                  </button>
                ) : (
                  line.value
                )}
              </dd>
            </div>
          ))}
          <div className="order-confirmation__row order-confirmation__row--total">
            <dt>Total</dt>
            <dd>
              {quote
                ? formatUsd(summary.total)
                : isQuoting
                  ? "Calculating…"
                  : formatUsd(summary.total)}
            </dd>
          </div>
        </dl>

        <div className="order-confirmation__contact">
          <h6 className="order-confirmation__section-title">Contact Information</h6>
          <p className="order-confirmation__contact-email mb-0">
            {audiologistEmail || "—"}
          </p>
        </div>

        <AudiologistDeliveryFields
          delivery={delivery}
          errors={deliveryErrors}
          onChange={updateDeliveryField}
          onPlaceSelect={handlePlaceSelect}
        />

        <div className="order-confirmation__payment-section">
          <h6 className="order-confirmation__payment-title">Payment Method</h6>
          <p className="order-confirmation__payment-note mb-2">
            Enter the audiologist card that will be charged.
          </p>
          <div className="order-confirmation__card-element">
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </div>
        </div>
      </div>
    </CommonModal>
  );
}

export default function OrderConfirmationModal(props) {
  const stripeConfigured = isStripeCheckoutConfigured();
  const stripePromise = getAudiologistStripePromise();

  if (!stripeConfigured || !stripePromise) {
    return (
      <CommonModal
        show={props.show}
        setShow={(open) => {
          if (!open) props.onClose();
        }}
        title="Confirmation"
        centered
        className="order-confirmation-modal modal-md"
        onHide={props.onClose}
        onBack={props.onBack}
        closeOnBackdrop={false}
        footer={
          <CommonModalFooter className="order-confirmation-footer d-flex gap-3 mt-4">
            <Button label="Cancel" className="cancel" onClick={props.onClose} />
          </CommonModalFooter>
        }
      >
        <p className="mb-0">
          Stripe is not configured. Add{" "}
          <code>VITE_STRIPE_PUBLISHABLE_KEY</code> to the frontend env to enable
          Confirm and Pay.
        </p>
      </CommonModal>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <ConfirmationCheckoutForm {...props} />
    </Elements>
  );
}
